import { Media } from "components";
import { useBoolean } from "hooks";
import {
  EProductVariantVisibility,
  ProductsListItem,
  ProductsSelector
} from "modules/products";
import { useTranslation } from "react-i18next";
import { Button, FormField } from "ui";

import { MAX_AD_PRODUCTS } from "../../../../constants";
import { productsListItemToPreview } from "../../../../helpers/variantAdapters";
import { VariantPreview } from "../../../../whatsappAdProducts.types";

import styles from "./VariantPickerField.module.scss";

interface VariantPickerFieldProps {
  value: string[];
  previews: VariantPreview[];
  errorMessage?: string;
  onChange: (variantIds: string[], previews: VariantPreview[]) => void;
}

const previewToListItem = (preview: VariantPreview): ProductsListItem =>
  ({
    id: preview.id,
    productId: preview.productId,
    categoryId: preview.categoryId,
    name: preview.name,
    image: preview.image,
    categoryName: preview.categoryName,
    colorAttributeValue: preview.colorValue
      ? { value: preview.colorValue }
      : undefined
  }) as unknown as ProductsListItem;

export const VariantPickerField = ({
  value,
  previews,
  errorMessage,
  onChange
}: VariantPickerFieldProps) => {
  const { t } = useTranslation();
  const { value: isPickerOpen, setTrue: openPicker, setFalse: closePicker } =
    useBoolean();

  const previewsById = new Map(previews.map((preview) => [preview.id, preview]));

  const handlePickerChange = (selected: ProductsListItem[]) => {
    const picked = selected.slice(0, MAX_AD_PRODUCTS);
    onChange(
      picked.map((item) => item.id),
      picked.map(productsListItemToPreview)
    );
  };

  const handleRemove = (variantId: string) => {
    onChange(
      value.filter((id) => id !== variantId),
      previews.filter((preview) => preview.id !== variantId)
    );
  };

  const renderSelected = () => {
    if (value.length === 0) {
      return <p className={styles.placeholder}>{t("No products selected")}</p>;
    }

    return (
      <ol className={styles.list}>
        {value.map((variantId) => {
          const preview = previewsById.get(variantId);
          const meta = preview
            ? [preview.categoryName, preview.colorValue].filter(Boolean).join(" · ")
            : "";

          return (
            <li key={variantId} className={styles.selected}>
              {preview?.image ? (
                <div className={styles.thumb}>
                  <Media mediaFile={preview.image} />
                </div>
              ) : null}
              <div className={styles.selectedText}>
                {preview ? (
                  <p className={styles.selectedName}>{preview.name}</p>
                ) : (
                  <p className={styles.selectedId}>{variantId}</p>
                )}
                {meta ? <p className={styles.selectedMeta}>{meta}</p> : null}
              </div>
              <button
                type="button"
                className={styles.remove}
                onClick={() => handleRemove(variantId)}
              >
                {t("Remove")}
              </button>
            </li>
          );
        })}
      </ol>
    );
  };

  return (
    <FormField label={t("Products")} errorMessage={errorMessage}>
      <div className={styles.container}>
        {renderSelected()}
        <Button type="button" variant="default" onClick={openPicker}>
          {value.length ? t("Change products") : t("Pick products")}
        </Button>
      </div>
      <ProductsSelector
        isOpen={isPickerOpen}
        onClose={closePicker}
        value={value.map((variantId) =>
          previewToListItem(
            previewsById.get(variantId) ?? ({ id: variantId, name: "" } as VariantPreview)
          )
        )}
        onChange={handlePickerChange}
        maxValueLength={MAX_AD_PRODUCTS}
        filterFn={(item) =>
          item.visibility === EProductVariantVisibility.Visible
        }
        title={t("Pick the products for this ad")}
      />
    </FormField>
  );
};
